import { useCallback, useRef, useState } from 'react';
import { UPLOAD_FAILED } from '../lib/fileLimits';
import { coreSync } from '../lib/core';

// The files a teacher has asked to upload, each with how far it has got
// (#577): the file's name and amount, so a long upload that is moving reads
// differently from one that failed.
//
// ── What each file goes through ─────────────────────────────────────────────
//
//   waiting   → picked, behind another file. One at a time: a drop of six on
//               a home connection is faster one after another than six at
//               once sharing it, and the first is finished soonest.
//   uploading → the bytes are going up; `transferred` of `total`.
//   failed    → refused, with the sentence `uploadFile` threw (already written
//               for a teacher — core/src/files.rs `upload_error_message`).
//               It stays on screen until it is tried again or dismissed: a
//               failure is something to act on, not something to read and
//               lose (Toast.jsx says the same about what a toast is for).
//
// A file that lands leaves the list, and `onUploaded(rows)` is told once the
// queue is empty, with every row that landed in that run — so six files make
// one "Uploaded 6 files." rather than six toasts replacing one another.
// `onLanded(row)` is told each one as it lands, for a caller that puts it
// somewhere at once (an expense's receipts, #612).
//
// Try again is the same call again, since the File is still held: a fresh
// authorisation (the daily allowance spends one more) and the bytes from the
// start. A refusal that will not go away on its own — the allowance, the byte
// ceiling — gets the same sentence back.
//
// Cancel (#612) takes a file off the list whatever it is doing: one waiting
// never starts, and one uploading has its upload aborted through the `signal`
// the upload function is handed, whose job it is to cancel the bytes and
// delete any that landed anyway (src/data/useFiles.jsx). A cancelled upload is
// not a failure and says nothing.
//
// A stalled upload fails like any other (#612): the upload function runs
// under the no-progress watchdog (src/lib/uploadWatch.js) and rejects with the
// timeout sentence, which this list shows with Try again.
//
// Every upload site on the web draws its rows through one component,
// src/components/UploadProgress.jsx, and both phones draw theirs the same way.

/** The words an upload row shows that are not a file's own name or sentence. */
export const UPLOAD_WORDS = Object.freeze({
  waiting: 'Waiting',
  uploading: 'Uploading',
  retry: 'Try again',
  dismiss: 'Dismiss',
  cancel: 'Cancel',
});

/** The toast after a run: the file's name for one, the count for more. */
export function uploadedSentence(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 1) return `Uploaded ${list[0]?.name || 'the file'}.`;
  return `Uploaded ${list.length} files.`;
}

// The amount and the percent are the core's (core/src/files.rs
// `upload_progress_text`, `upload_percent`), since the phones show the same
// row (#611, #612).
const amount = (n) => Number(n) || 0;

/** "45% · 63.2 MB of 140.2 MB" — the two sizes in the library's own units. */
export function uploadProgressText({ transferred = 0, total = 0 } = {}) {
  return coreSync().uploadProgressText(amount(transferred), amount(total));
}

/** Whole percent, never 100 until every byte is there. */
export function uploadPercent({ transferred = 0, total = 0 } = {}) {
  return coreSync().uploadPercent(amount(transferred), amount(total));
}

let nextKey = 0;

function entries(files, folderId, status, message = null) {
  nextKey += 1;
  const base = nextKey;
  return files.map((file, i) => ({
    key: `${base}.${i}`,
    file,
    folderId,
    name: file?.name || 'file',
    size: file?.size || 0,
    status,
    transferred: 0,
    total: file?.size || 0,
    message,
  }));
}

/**
 * The list over `uploadFile(file, { folderId, onProgress, signal })`, which
 * resolves with the row that landed or rejects with a sentence — or with an
 * error whose `cancelled` is true, when `signal` aborted it.
 */
export default function useUploads(uploadFile, { onUploaded, onLanded } = {}) {
  const [items, setItems] = useState([]);
  // The list as the runner last left it. Read by the loop below, which must
  // see a file added mid-run, and which a state closure would not show it.
  const current = useRef([]);
  const running = useRef(null);
  const landed = useRef([]);
  // Each uploading file's AbortController, by key.
  const controllers = useRef(new Map());
  const told = useRef({ onUploaded, onLanded });
  told.current = { onUploaded, onLanded };

  const update = useCallback((change) => {
    current.current = change(current.current);
    setItems(current.current);
  }, []);
  const patch = useCallback((key, fields) => {
    update((list) => list.map((it) => (it.key === key ? { ...it, ...fields } : it)));
  }, [update]);

  // The run, as a promise that settles when the queue is empty — what `add`
  // and `retry` hand back, so a caller can wait for its files.
  const run = useCallback(() => {
    if (running.current) return running.current;
    const listed = (key) => current.current.some((it) => it.key === key);
    running.current = (async () => {
      for (;;) {
        const next = current.current.find((it) => it.status === 'waiting');
        if (!next) break;
        const controller = new AbortController();
        controllers.current.set(next.key, controller);
        patch(next.key, { status: 'uploading', transferred: 0, total: next.size, message: null });
        const outcome = await uploadFile(next.file, {
          folderId: next.folderId,
          signal: controller.signal,
          onProgress: ({ transferred, total }) => {
            if (listed(next.key)) patch(next.key, { transferred, total: total || next.size });
          },
        }).then(
          (row) => ({ row: row || { name: next.name } }),
          (err) => (err?.cancelled ? { cancelled: true } : { message: err?.message || UPLOAD_FAILED }),
        );
        controllers.current.delete(next.key);
        if (outcome.cancelled || !listed(next.key)) {
          update((list) => list.filter((it) => it.key !== next.key));
        } else if (outcome.row) {
          update((list) => list.filter((it) => it.key !== next.key));
          landed.current.push(outcome.row);
          if (told.current.onLanded) told.current.onLanded(outcome.row, next);
        } else {
          patch(next.key, { status: 'failed', message: outcome.message });
        }
      }
      running.current = null;
      const rows = landed.current;
      landed.current = [];
      if (rows.length && told.current.onUploaded) told.current.onUploaded(rows);
    })();
    return running.current;
  }, [uploadFile, patch, update]);

  const add = useCallback((files, { folderId = null } = {}) => {
    const picked = Array.from(files || []);
    if (!picked.length) return running.current || Promise.resolve();
    update((list) => [...list, ...entries(picked, folderId, 'waiting')]);
    return run();
  }, [update, run]);

  // A file whose upload failed somewhere else — the receipt scan's, which runs
  // beside the reader rather than through this list — listed as failed, so it
  // has the same sentence and Try again as one that failed here.
  const fail = useCallback((file, message, { folderId = null } = {}) => {
    if (!file) return;
    update((list) => [...list, ...entries([file], folderId, 'failed', message || UPLOAD_FAILED)]);
  }, [update]);

  const retry = useCallback((key) => {
    patch(key, { status: 'waiting', transferred: 0, message: null });
    return run();
  }, [patch, run]);

  const dismiss = useCallback((key) => {
    update((list) => list.filter((it) => it.key !== key));
  }, [update]);

  // Off the list whatever it is doing; an upload in flight is aborted.
  const cancel = useCallback((key) => {
    const controller = controllers.current.get(key);
    update((list) => list.filter((it) => it.key !== key));
    if (controller) controller.abort();
  }, [update]);

  // Everything off and every upload aborted: the form they were for closed.
  const clear = useCallback(() => {
    update(() => []);
    for (const controller of controllers.current.values()) controller.abort();
  }, [update]);

  return {
    items,
    busy: items.some((it) => it.status !== 'failed'),
    add,
    fail,
    retry,
    dismiss,
    cancel,
    clear,
  };
}
